import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignScoutingAPIClient } from '@/marketing/infrastructure/influencer/campaign/scouting/campaignScoutingAPIClient';
import { refreshActiveScoutingPage, markRelatedScoutingDataStale } from './scoutingCache';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import { extractErrorMessage } from '@/marketing/lib/errorMessage';
import type { ScoutingDecision, ScoutingSelectionStage } from '@/marketing/models/campaignScouting';
import {
  canReviewScouting,
  type ScoutingCandidateSnapshot,
  type ScoutingViewRow,
} from './scoutingView';

interface ScoutingSelectionSnapshot {
  candidates: ScoutingCandidateSnapshot[];
  targetStage: ScoutingSelectionStage;
}

interface ScoutingDecisionMutation {
  row: ScoutingViewRow;
  next: ScoutingDecision;
  reason?: string;
}

export function useScoutingActions(campaignId: string, setChecked: Dispatch<SetStateAction<Set<string>>>) {
  const client = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const transitionBy = user?.email;
  const [snapshot, setSnapshot] = useState<ScoutingSelectionSnapshot | null>(null);
  const [declineTarget, setDeclineTarget] = useState<ScoutingViewRow | null>(null);
  const decisionInFlight = useRef(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const invalidate = () => {
    markRelatedScoutingDataStale(client);
    return refreshActiveScoutingPage(client, campaignId);
  };
  const decision = useMutation({
    mutationFn: ({ row, next, reason }: ScoutingDecisionMutation) => {
      if (!transitionBy?.trim()) {
        throw new Error('Sign in again before changing scouting decisions.');
      }
      return campaignScoutingAPIClient.decide(campaignId, row.influencerId, {
        decision: next,
        expectedVersion: row.version,
        transitionBy,
        ...(next === 'Declined' && reason?.trim() ? { reason: reason.trim() } : {}),
      });
    },
    onMutate: () => {
      setError('');
      setNotice('');
    },
    onSuccess: (_response, { row, next }) => {
      if (next === 'Declined') {
        setDeclineTarget(null);
      }
      if (next !== 'Finalist') {
        setChecked((current) => {
          const updated = new Set(current);
          updated.delete(row.influencerId);
          return updated;
        });
      }
      setNotice(
        next === 'Declined'
          ? 'Creator declined and marked rejected.'
          : `Creator marked ${next}. Pipeline stage is unchanged.`,
      );
    },
    onError: (failure) =>
      setError(extractErrorMessage(failure, 'Could not save the scouting decision. Refresh and try again.')),
    onSettled: async () => {
      try {
        await invalidate();
      } finally {
        decisionInFlight.current = false;
      }
    },
  });
  const selection = useMutation({
    mutationFn: ({ candidates, targetStage }: ScoutingSelectionSnapshot) => {
      if (!transitionBy?.trim()) {
        throw new Error('Sign in again before changing scouting decisions.');
      }
      return campaignScoutingAPIClient.select(campaignId, {
        candidates: candidates.map(({ influencerId, expectedVersion }) => ({ influencerId, expectedVersion })),
        confirmed: true,
        targetStage,
        transitionBy,
      });
    },
    onMutate: () => {
      setError('');
      setNotice('');
    },
    onSuccess: (response, { candidates, targetStage }) => {
      const results = new Map(response.results.map((result) => [result.influencerId, result]));
      const succeeded = candidates.filter((candidate) => results.get(candidate.influencerId)?.success === true);
      const failed = candidates.filter((candidate) => results.get(candidate.influencerId)?.success !== true);
      setChecked((current) => {
        const updated = new Set(current);
        for (const candidate of succeeded) {
          updated.delete(candidate.influencerId);
        }
        for (const candidate of failed) {
          updated.add(candidate.influencerId);
        }
        return updated;
      });
      const warnings = succeeded.flatMap((candidate) => {
        const warning = results.get(candidate.influencerId)?.warning;
        return warning ? [`${candidate.handle ?? candidate.influencerId}: ${warning}`] : [];
      });
      setNotice([`${targetStage === 'Brand Shortlisted' ? 'Brand selected' : 'Selected'} ${succeeded.length} creator(s).`, ...warnings].join(' '));
      if (failed.length) {
        setError(
          failed
            .map(
              (candidate) =>
                `${candidate.handle ?? candidate.influencerId}: ${results.get(candidate.influencerId)?.error ?? 'No successful result returned.'}`,
            )
            .join(' ') + ' Failed candidates remain checked. Review refreshed rows before trying again.',
        );
      }
      setSnapshot(null);
    },
    onError: (failure) => {
      setError(
        extractErrorMessage(failure, 'Could not confirm selection.') +
          ' Checked candidates are preserved. Review refreshed rows before trying again.',
      );
      setSnapshot(null);
    },
    onSettled: invalidate,
  });
  const openConfirmation = (candidates: ScoutingCandidateSnapshot[], targetStage: ScoutingSelectionStage = 'Shortlisted') => {
    if (decisionInFlight.current || !candidates.length) {
      return;
    }
    setError('');
    setDeclineTarget(null);
    setSnapshot({ candidates, targetStage });
  };
  const submitDecision = (row: ScoutingViewRow, next: ScoutingDecision, reason?: string) => {
    if (decisionInFlight.current || selection.isPending || !canReviewScouting(row)) {
      return;
    }
    decisionInFlight.current = true;
    decision.mutate({ row, next, ...(reason === undefined ? {} : { reason }) });
  };
  return {
    error,
    notice,
    snapshot,
    declineTarget,
    openConfirmation,
    cancelConfirmation: () => {
      if (!selection.isPending) {
        setSnapshot(null);
      }
    },
    confirm: () => {
      if (snapshot && !selection.isPending) {
        selection.mutate(snapshot);
      }
    },
    decide: (row: ScoutingViewRow, next: ScoutingDecision) => {
      if (decisionInFlight.current || selection.isPending || !canReviewScouting(row)) {
        return;
      }
      if (next === 'Declined') {
        setError('');
        setSnapshot(null);
        setDeclineTarget({ ...row });
      } else {
        submitDecision(row, next);
      }
    },
    cancelDecline: () => {
      if (!decisionInFlight.current) {
        setDeclineTarget(null);
      }
    },
    confirmDecline: (reason: string) => {
      if (declineTarget) {
        submitDecision(declineTarget, 'Declined', reason);
      }
    },
    declinePending: decision.isPending,
    pending: decision.isPending || selection.isPending,
    selectionPending: selection.isPending,
  };
}
